<template>
  <div>
    <Transition name="fade" mode="out-in">
      <Suspense @resolve="initAnimation()">
        <main class="relative overflow-hidden">
          <GridsOutterGrid>
            <SectionSet v-if="!error" is-first class="col-span-1 col-start-2">
              <GridsTwelveGrid>
                <BreadCrumbs class="col-span-6 col-start-1 laptop:col-span-5 laptop:col-start-2" />
                <BrandsBrandProductIntro
                  class="col-span-6 col-start-1 mt-1 laptop:col-span-12 laptop:col-start-1 laptop:mt-4"
                  :brand="data?.products?.data?.[0].attributes?.product_brand?.data"
                  :product="data?.products?.data?.[0]"
                />
              </GridsTwelveGrid>
            </SectionSet>
            <SectionSet class="col-span-1 col-start-2 empty:hidden">
              <BrandsBrandProductVariationsSection
                :product="data?.products?.data?.[0]"
                :brand="data?.products?.data?.[0].attributes?.product_brand?.data"
              />
            </SectionSet>
            <SectionSet
              class="col-span-1 col-start-2 empty:hidden"
              v-if="productSizes.data.value?.productSizes?.data.length"
            >
              <GridsTwelveGrid>
                <div class="col-span-6 col-start-1 empty:hidden laptop:col-span-10 laptop:col-start-2">
                  <h2 class="mb-8 flex w-full items-center">
                    {{ $t("available-packaging") }}
                    <span class="ml-4 hidden h-[40px] w-[40px] items-center justify-center laptop:flex">
                      <img src="/icons/frame.svg" width="25" height="25" />
                    </span>
                  </h2>
                  <ProductSizeTables :data="productSizes.data" :with-images="true" />
                </div>
              </GridsTwelveGrid>
            </SectionSet>
            <LeafAnimation />
            <SectionSet
              v-if="!error && brandsProductsFiltered && brandsProductsFiltered.length"
              class="col-span-3 col-start-1 laptop:col-span-1 laptop:col-start-2"
            >
              <div class="grid-wrapper grid grid-cols-6 gap-x-6 laptop:grid-cols-12">
                <div class="col-span-6 col-start-1 laptop:col-span-10 laptop:col-start-2">
                  <SliderBrands
                    :products="brandsProductsFiltered"
                    :brand="data?.products?.data?.[0].attributes?.product_brand?.data?.attributes?.slug"
                    :title="
                      $t('more_products_from_brand', {
                        brand: data?.products?.data?.[0].attributes?.product_brand?.data?.attributes?.name,
                      })
                    "
                    :buttonText="
                      $t('see_other_brand_products', {
                        brand: data?.products?.data?.[0].attributes?.product_brand?.data?.attributes?.name,
                      })
                    "
                    :link="
                      localePath({
                        name: 'brands',
                        query: { brand: data?.products?.data?.[0].attributes?.product_brand?.data?.attributes?.slug },
                      })
                    "
                  />
                </div>
              </div>
            </SectionSet>
            <SectionSet v-if="!error" class="col-span-1 col-start-2 mt-8 laptop:mt-10" isLast>
              <GridsTwelveGrid>
                <h4 class="col-span-6 col-start-1 mt-25 text-center laptop:col-span-6 laptop:col-start-4 laptop:mt-36">
                  {{ $t("our-products-are-also-available-for-private-label-and-in-foodservice-quantities-0") }}
                </h4>
                <div
                  class="col-span-6 col-start-1 mt-8 flex w-full flex-col space-y-6 laptop:col-span-10 laptop:col-start-2 laptop:mt-10 laptop:flex-row laptop:space-x-6 laptop:space-y-0"
                >
                  <LinkCard
                    bg="bg-[#F6EEEB]"
                    :title="$t('private-label')"
                    :subtitle="$t('customized-products-with-your-label')"
                    img="/images/jam-jar.png"
                    :link="localePath({ name: 'for-business-private-label' })"
                    arrow="#B06C4C"
                  />
                  <LinkCard
                    bg="bg-[#EBF5F1]"
                    :title="$t('packaging')"
                    :subtitle="$t('See Packaging options & Logistics')"
                    img="/images/packaging.png"
                    :link="localePath({ name: 'products-packaging' })"
                    arrow="#647E8D"
                  />
                </div>
              </GridsTwelveGrid>
              <div class="mt-6 flex w-full items-center justify-center">
                <NextBtn :text="$t('contact-us')" :to="localePath({ name: 'contact' })" />
              </div>
            </SectionSet>
          </GridsOutterGrid>
        </main>

        <template #fallback>
          <LoadingSpinner />
        </template>
      </Suspense>
    </Transition>
  </div>
</template>
<script setup lang="ts">
import { gsap } from "gsap";
import { LoadingBarKey } from "~/symbols/symbols";
import { useWaitForPageTransitionEnd } from "~/composables/waitPageTransition";

const loadingBarIndicator = inject(LoadingBarKey);

const localePath = useLocalePath();

const { locale } = useI18n();

const error = ref(false);

definePageMeta({
  validate: async (route) => {
    if (route.params.product === undefined || route.query.id === undefined) return false;

    const { data } = await useAsyncGql({
      operation: "CheckIfProductExists",
      variables: { slug: route.params.product as string, id: route.query.id as string },
      options: {
        lazy: false,
      },
    });

    if (data?.value?.products?.data.length === 0) {
      return false;
    } else {
      return true;
    }
  },
});

const route = useRoute();

const { data } = await useAsyncGql({
  operation: "GetProductBySlug",
  variables: { slug: route.params.product as string, id: route.query.id as string },
  options: {
    lazy: false,
  },
});

const productSizes = await useAsyncGql({
  operation: "ProductSizesBySlug",
  variables: {
    product_slug: route.params.product as string,
    product_id: route.query.id as string,
    locale: locale.value,
  },
  options: {
    lazy: false,
  },
});

const brandsProductsFiltered = computed(() => {
  return data?.value?.products?.data?.[0].attributes?.product_brand?.data?.attributes?.products?.data?.filter(
    (product) => {
      return product.attributes?.slug !== data.value?.products?.data?.[0]?.attributes?.slug;
    },
  );
});

const leaf = ref<HTMLElement | null>(null);

let tl;

const { promise } = useWaitForPageTransitionEnd();

async function initAnimation() {
  console.log("init animation");
  await promise.value;

  console.log("init animation2");

  await nextTick();

  console.log("init animation3");

  if (leaf.value) {
    console.log("leaf");
    tl = gsap.timeline({
      scrollTrigger: {
        trigger: leaf.value,
        start: "center 30%",
        end: "+=100",
        scrub: 3,
      },
    });

    tl.to(leaf.value, {
      motionPath: {
        path: "M430,47 c26,314 -250,394 -349,200",
        align: leaf.value,
        alignOrigin: [0.5, 0.5],
      },
      ease: "ease.out",
      rotate: 120,
    });
  }
}

onBeforeUnmount(() => {
  if (tl) {
    tl.kill();
    tl = null;
  }
});
</script>
<style lang="scss" scoped></style>
